import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

type SessionInfo = {
  id: string;
  access: string;
  sessionFormType: string;
  role?: string;
};

type View = { pageId: string; sectionId?: string };
type SetViewOptions = { scroll?: boolean; replace?: boolean };

type AppContextProps = {
  currentPageId: string | undefined;
  currentSectionId: string | undefined;
  setCurrentView: (view: View, options?: SetViewOptions) => void;
  setCurrentPageId: (pageIndex: string, scroll?: boolean) => void;
  completedNavigationItemsIds: string[];
  setCompletedNavigationItemsIds: (ids: string[]) => void;
  sessionInfo: SessionInfo;
  currentRoute: string;
};

export const AppContext = createContext<AppContextProps>({
  currentPageId: undefined,
  currentSectionId: undefined,
  setCurrentView: () => '',
  setCurrentPageId: () => '',
  completedNavigationItemsIds: [],
  setCompletedNavigationItemsIds: () => '',
  sessionInfo: { id: '', access: '', sessionFormType: '' },
  currentRoute: 'landing-page',
});

/** Next.js exposes repeated query params as arrays; take the first value. */
const firstQueryValue = (
  value: string | string[] | undefined,
): string | undefined => (Array.isArray(value) ? value[0] : value);

export const AppContextProvider = ({
  children,
  sessionInfo,
}: {
  children: ReactNode;
  sessionInfo: SessionInfo;
}) => {
  const router = useRouter();

  // The URL is the source of truth for the current view. Deriving both values
  // from the query makes browser back/forward and the device back gesture
  // change the screen without a popstate listener.
  const currentPageId = router.isReady
    ? firstQueryValue(router.query.page)
    : undefined;
  const currentSectionId = router.isReady
    ? firstQueryValue(router.query.section)
    : undefined;

  const [completedNavigationItemsIds, setCompletedNavigationItemsIds] =
    useState<string[]>([]);

  const setCurrentView = useCallback(
    (
      { pageId, sectionId }: View,
      { scroll = true, replace = false }: SetViewOptions = {},
    ) => {
      if (
        !pageId ||
        (pageId === currentPageId && sectionId === currentSectionId)
      ) {
        return;
      }
      document.querySelector('main')?.scrollTo(0, 0);
      const { section: _section, ...query } = router.query;
      router[replace ? 'replace' : 'push'](
        {
          query: {
            ...query,
            page: pageId,
            ...(sectionId ? { section: sectionId } : {}),
          },
        },
        undefined,
        { scroll },
      );
    },
    [router, currentPageId, currentSectionId],
  );

  const setCurrentPageId = useCallback(
    (id: string, scroll: boolean = true) =>
      setCurrentView({ pageId: id }, { scroll }),
    [setCurrentView],
  );

  // Both /form and the path-based route render the same form UI, so both track currentPageId.
  const isFormRoute =
    router.pathname === '/form' ||
    router.pathname === '/form/[formType]/[[...rest]]';
  const currentRoute = isFormRoute
    ? currentPageId || 'landing-page'
    : router.pathname.split('/').pop() || '';

  return (
    <AppContext.Provider
      value={{
        currentPageId,
        currentSectionId,
        setCurrentView,
        setCurrentPageId,
        completedNavigationItemsIds,
        setCompletedNavigationItemsIds,
        sessionInfo,
        currentRoute,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextProps => useContext(AppContext);
